import * as React from "react"

import { cn } from "./cn"

/**
 * A label for a control. In a module it is used through `Field`; on its own
 * only where a control has no field around it, such as a filter in a toolbar.
 */
function Label({ className, ...props }: React.ComponentProps<"label">) {
  return (
    <label
      data-slot="label"
      className={cn("text-xs text-gray-500 avex:text-[0.75rem] avex:font-medium avex:text-ink-secondary", className)}
      {...props}
    />
  )
}

/**
 * A control with its label and an optional hint: the one shape every form in a
 * module takes, so the spacing between label and field, and the label's look,
 * are decided here and not in each module.
 *
 * `inline` puts the label beside the control, for a filter row; the default
 * puts it above, for a form.
 */
function Field({
  label,
  htmlFor,
  hint,
  inline = false,
  className,
  children,
  ...props
}: Omit<React.ComponentProps<"div">, "children"> & {
  label: React.ReactNode
  /** The id of the control, so a click on the label focuses it. */
  htmlFor?: string
  hint?: React.ReactNode
  inline?: boolean
  children: React.ReactNode
}) {
  return (
    <div
      data-slot="field"
      className={cn(inline ? "flex items-center gap-2" : "flex min-w-0 flex-col gap-1", className)}
      {...props}
    >
      <Label htmlFor={htmlFor} className={inline ? "text-sm" : undefined}>
        {label}
      </Label>
      {children}
      {hint !== undefined && (
        <p className="text-xs text-gray-400 avex:text-ink-muted">{hint}</p>
      )}
    </div>
  )
}

/**
 * A native checkbox with its label to the right. Native on purpose, as the
 * stylesheet shapes every checkbox alike; what the kit decides is the label and
 * the distance between the two. Without `label` it is the bare box, for a table
 * cell whose column head says what it means — give it an `aria-label` then.
 */
function Checkbox({
  label,
  className,
  ...props
}: Omit<React.ComponentProps<"input">, "type"> & { label?: React.ReactNode }) {
  const box = <input type="checkbox" data-slot="checkbox" className={label === undefined ? className : undefined} {...props} />
  if (label === undefined) return box
  return (
    <label
      className={cn(
        "inline-flex items-center gap-1.5 text-sm text-gray-600 avex:text-ink-secondary",
        props.disabled && "opacity-50",
        className
      )}
    >
      {box}
      {label}
    </label>
  )
}

export { Checkbox, Field, Label }
